import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';

import registerLogger, { ErrorBoundary, getSourceMapLocation } from '@eika-infrastruktur/log';
import { loadContainers } from '@eika-infrastruktur/render';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { CustomAppType as CustomAppTypeV3 } from '@/custom-app-types';
import { Config, DEFAULT_CONFIG, REPO_NAMESPACE } from '@/default-config';
import { useMocks } from '@/domain/mock';
import { enableMocking } from '@/mocks';
import ApplePayProvision from '@/widgets/ApplePayProvision/ApplePayProvision';
import ApplePayProvisionTipsCard from '@/widgets/ApplePayProvisionTipsCard/ApplePayProvisionTipsCard';
import Click2PayLinkCard from '@/widgets/Click2Pay/Click2PayLinkCard';
import PendingCardTokens from '@/widgets/PendingCardTokens/PendingCardTokens';

import { SessionLoader } from '@components/app';
import { V3App } from '@components/app/V3App';
import { Logger } from '@domain/app';
import { RESOURCE_PATH } from '@utils/url-utils';

import { VippsWidget } from './widgets/Vipps/VippsWidget';

import './main.scss';

const loggerUrl = `${RESOURCE_PATH}/log`;
registerLogger(loggerUrl, REPO_NAMESPACE);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            throwOnError: (error, orgQuery) => {
                if (!(error instanceof RequestExceptionWithResponse)) {
                    const errorType = error as Error;
                    Logger.error('query', errorType.message, 'queryClient', {
                        queryKey: orgQuery?.queryKey,
                        orgError: error,
                        errorName: errorType?.name,
                        stack: errorType?.stack,
                        url: window.location.href,
                    });
                }
                return false;
            },
            staleTime: Infinity,
            retry: 1,
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: false,
            retryOnMount: false,
        },
    },
});

const getSourceMapError = getSourceMapLocation(REPO_NAMESPACE);

const mapLogError = (namespace: string) => async (error: Error, errorInfo: React.ErrorInfo) =>
    Logger.error('error-bundary', error.message, 'index', {
        ...error,
        errorName: error.name,
        stack: error.stack,
        errorInfo,
        url: window.location.href,
        namespace,
        sourceMapLocation: await getSourceMapError(errorInfo),
    });

const renderAppWithQueryClient = (
    App: React.ComponentType,
    namespace: string,
    appDefaultConfig: Config = DEFAULT_CONFIG,
    showSessionLoading = true,
): void => {
    loadContainers(
        namespace,
        () => {
            return (
                <ErrorBoundary onError={mapLogError(REPO_NAMESPACE)}>
                    <React.StrictMode>
                        <div className="sgw-content kort-betjening betjening-web-card v3">
                            <QueryClientProvider client={queryClient}>
                                <SessionLoader showLoading={showSessionLoading}>
                                    <App />
                                </SessionLoader>
                                {process.env.NODE_ENV !== 'production' ? <ReactQueryDevtools /> : null}
                            </QueryClientProvider>
                        </div>
                    </React.StrictMode>
                </ErrorBoundary>
            );
        },
        appDefaultConfig,
    );
};

enableMocking(useMocks).then(() => {
    renderAppWithQueryClient(VippsWidget, CustomAppTypeV3.VIPPS, DEFAULT_CONFIG, false);
    renderAppWithQueryClient(ApplePayProvision, CustomAppTypeV3.APPLE_PAY_PROVISION, DEFAULT_CONFIG, false);
    renderAppWithQueryClient(
        ApplePayProvisionTipsCard,
        CustomAppTypeV3.APPLE_PAY_PROVISION_TIPS_CARD,
        DEFAULT_CONFIG,
        false,
    );
    renderAppWithQueryClient(Click2PayLinkCard, CustomAppTypeV3.C2P_LINK_CARD, DEFAULT_CONFIG, false);
    renderAppWithQueryClient(PendingCardTokens, CustomAppTypeV3.PENDING_CARD_TOKENS, DEFAULT_CONFIG, false);
    renderAppWithQueryClient(V3App, CustomAppTypeV3.V3, DEFAULT_CONFIG, true);
});
